/**
 * The engine as a browser bundler gets it.
 *
 * Deliberately the `worker` entry rather than the default browser one. Both are
 * the same Rust engine; they differ in how the WASM is instantiated. The
 * browser entry is the wasm-pack *bundler* target and instantiates at module
 * load through `import * as wasm from "./forme_bg.wasm"` — an ES module
 * integration import that Angular's builder refuses outright unless the host
 * application has gone zoneless. The `worker` entry is the *web* target and
 * initialises explicitly, so it is an ordinary module to every bundler.
 *
 * The cost is that somebody has to say where the `.wasm` is. Left alone,
 * Forme's glue asks for `forme_bg.wasm` next to its own module URL, which Vite
 * and Webpack resolve by emitting it as a build asset. Where that does not
 * happen — or where the file is served from a CDN — the host calls
 * `setFormeWasm()` once at startup with a URL, a Response or the bytes.
 */
import { init, renderSerializedDocWithLayout } from "@formepdf/core/worker";
import type { EngineLoader } from "./api.js";
import type { FontFileReader } from "./fonts.js";

/** In a browser a theme font's relative address is relative to the page, as `@font-face` reads it. */
export const readFontFile: FontFileReader = async (src) => {
  const response = await fetch(src);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return new Uint8Array(await response.arrayBuffer());
};

let wasm: unknown;

/**
 * Where to load the engine's WASM from: a `URL`, a `Response`, a
 * `Promise<Response>`, raw bytes, or an already-compiled `WebAssembly.Module`.
 *
 * Call it before the first render. Afterwards it has no effect — the engine is
 * instantiated once and kept.
 */
export function setFormeWasm(source: URL | Response | Promise<Response> | BufferSource | WebAssembly.Module): void {
  wasm = source;
}

export const loadEngine: EngineLoader = async () => {
  await init(wasm);
  return (doc) => renderSerializedDocWithLayout(doc) as Promise<{ pdf: Uint8Array; layout: unknown; warnings: string[] }>;
};
